import {skipToken} from '@reduxjs/toolkit/query';
import {useLocale} from 'platform/locale';

import {useEffect, useState} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNil, isNotNil} from 'ramda';

import {useAddrDetailQuery, useAddrSuggestionsQuery} from '../../../store/addressApi';
import {AddrSuggestionsApiResponse} from '../../../types/api/api';
import {FilterFormAddress, FilterFormData} from '../../../types/FilterFormData';
import {PriceReportType} from '../../../types/PriceReportType';

const getPriceReportAddress = (priceReport: PriceReportType) =>
  priceReport.vehicle.location?.address ?? null;

const hasCoordinates = (address: FilterFormAddress | null | undefined) =>
  isNotNil(address?.lat) && isNotNil(address?.lng);

const getFirstSuggestionWithPlaceId = (suggestions: AddrSuggestionsApiResponse | undefined) =>
  suggestions?.find((suggestion) => typeof suggestion?.placeId === 'string');

export function useLookupDefaultAddress({
  formApi,
  priceReport,
}: {
  formApi: UseFormReturn<FilterFormData>;
  priceReport: PriceReportType;
}): FilterFormAddress | null {
  const {language} = useLocale();
  const [resolvedDefaultAddress, setResolvedDefaultAddress] = useState<FilterFormAddress | null>(
    null
  );
  const address = useWatch({control: formApi.control, name: 'address'});
  const priceReportAddress = getPriceReportAddress(priceReport);
  const shouldLookupDefaultAddress =
    isNotNil(priceReportAddress) &&
    address?.label === priceReportAddress &&
    !hasCoordinates(address);

  const {data: suggestions} = useAddrSuggestionsQuery(
    shouldLookupDefaultAddress
      ? {term: priceReportAddress, country: priceReport.market, language}
      : skipToken
  );
  const firstSuggestion = getFirstSuggestionWithPlaceId(suggestions);
  const {data: addressDetail} = useAddrDetailQuery(
    shouldLookupDefaultAddress && typeof firstSuggestion?.placeId === 'string'
      ? {placeId: firstSuggestion.placeId, language}
      : skipToken
  );

  // useEffect: push resolved coordinates into RHF when the chained address lookup completes.
  // Not derived state (RHF is an external imperative store), not an event handler (async RTK
  // Query), not key remount (would discard unrelated filter state), not useOnMount (data arrives
  // after mount once suggestions and place detail resolve).
  useEffect(
    function fillDefaultAddressCoordinates() {
      if (!shouldLookupDefaultAddress || isNil(priceReportAddress)) {
        return;
      }

      if (isNil(addressDetail) || typeof addressDetail !== 'object' || isNil(addressDetail.gps)) {
        return;
      }

      const lat = parseFloat(addressDetail.gps.lat);
      const lng = parseFloat(addressDetail.gps.lng);

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return;
      }

      const resolvedAddress = {
        label: firstSuggestion?.formattedAddress ?? priceReportAddress,
        lat,
        lng,
      };

      formApi.setValue('address', resolvedAddress, {
        shouldDirty: false,
        shouldTouch: false,
      });
      setResolvedDefaultAddress(resolvedAddress);
    },
    [addressDetail, firstSuggestion, formApi, priceReportAddress, shouldLookupDefaultAddress]
  );

  return resolvedDefaultAddress;
}
